import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { experimentApi } from '../../../apiHooks/experimentApi';
import { IExperiment, IExperimentResult } from '../../../apiHooks/experimentApi.types';
import { resourceApi } from '../../../apiHooks/resourceApi';
import { RootState, store } from '../../../state';
import { mapJobStateToResultStatus, mapResultStatusToJobState } from './job';

type AppDispatch = ThunkDispatch<RootState, undefined, AnyAction>;

const dispatch: AppDispatch = (action) => (store.dispatch as AppDispatch)(action);

function validatePayload(raw: Record<string, unknown>, allowedKeys: string[]): string | null {
    const unexpectedKeys = Object.keys(raw).filter((key) => !allowedKeys.includes(key));
    if (unexpectedKeys.length > 0) {
        return `Unexpected keys: ${unexpectedKeys.join(', ')}`;
    }
    return null;
}

export interface IFrameExperiment {
    uuid: string | null;
    name: string | null;
    uri: string | null;
    createdAt: string | null;
}

export interface IFrameExperimentResult {
    id: string;
    name: string;
    createdAt: string;
    finishedAt: string;
    state: string;
    appUri: string;
}

export interface IFramePaginatedResponse<T> {
    object_count: number;
    page_count: number;
    results: T[];
}

function toIFrameExperiment(experiment: IExperiment): IFrameExperiment {
    return {
        uuid: experiment.uuid,
        name: experiment.name,
        uri: experiment.uri,
        createdAt: experiment.created_at,
    };
}

async function resolveExperimentUuid(uri: string): Promise<string> {
    const dispatchResult = dispatch(
        resourceApi.endpoints.getResource.initiate({ uri }, { forceRefetch: true }),
    );
    try {
        const resource = await dispatchResult.unwrap();
        if (resource.type !== 'experiment') {
            throw `Experiment with uri '${uri}' not found`;
        }
        return resource.uuid;
    } finally {
        dispatchResult.unsubscribe();
    }
}

export async function getExperiment(
    requestPayload: Record<string, unknown>,
): Promise<IFrameExperiment> {
    const validationError = validatePayload(requestPayload, ['uri', 'id']);
    if (validationError) {
        throw validationError;
    }
    const id = requestPayload.id as string | undefined;
    const uri = requestPayload.uri as string | undefined;
    if (!id && !uri || id && uri) {
        throw 'Either uri or id must be provided';
    }
    const experimentUuid = id || await resolveExperimentUuid(uri!);
    const dispatchResult = dispatch(
        experimentApi.endpoints.getExperiment.initiate({ uuid: experimentUuid }, { forceRefetch: true }),
    );
    try {
        const experiment = await dispatchResult.unwrap();
        return toIFrameExperiment(experiment);
    } finally {
        dispatchResult.unsubscribe();
    }
}

export async function createExperiment(
    requestPayload: Record<string, unknown>,
): Promise<IFrameExperiment> {
    const validationError = validatePayload(requestPayload, ['uri']);
    if (validationError) {
        throw validationError;
    }
    if (!requestPayload.uri) {
        throw 'uri is required';
    }
    const body = { uri: String(requestPayload.uri) };
    const result = await dispatch(experimentApi.endpoints.createExperiment.initiate(body)).unwrap();
    return toIFrameExperiment(result);
}

export async function listExperiments(
    requestPayload: Record<string, unknown>,
): Promise<IFramePaginatedResponse<IFrameExperiment>> {
    const validationError = validatePayload(requestPayload, [
        'page',
        'page_size',
        'search',
        'ordering',
        'include_intrinsic',
        'include_shared',
    ]);
    if (validationError) {
        throw validationError;
    }
    const payload = {
        page: requestPayload.page as number | undefined,
        page_size: requestPayload.page_size as number | undefined,
        search: requestPayload.search as string | undefined,
        ordering: requestPayload.ordering as string | undefined,
        include_intrinsic: requestPayload.include_intrinsic === true ? 'true' : 'false',
        include_shared: requestPayload.include_shared === true ? 'true' : 'false',
    };
    const dispatchResult = dispatch(
        experimentApi.endpoints.listExperiments.initiate(payload, { forceRefetch: true }),
    );
    try {
        const response = await dispatchResult.unwrap();
        return {
            object_count: response.object_count,
            page_count: response.page_count,
            results: response.results.map(toIFrameExperiment),
        };
    } finally {
        dispatchResult.unsubscribe();
    }
}

export async function listExperimentResults(
    requestPayload: Record<string, unknown>,
): Promise<IFramePaginatedResponse<IFrameExperimentResult>> {
    const validationError = validatePayload(requestPayload, [
        'uri',
        'id',
        'page',
        'page_size',
        'ordering',
        'state',
        'search',
    ]);
    if (validationError) {
        throw validationError;
    }
    const id = requestPayload.id as string | undefined;
    const uri = requestPayload.uri as string | undefined;
    if (!id && !uri || id && uri) {
        throw 'Either uri or id must be provided';
    }
    const experimentUuid = id || await resolveExperimentUuid(uri!);
    const payload = {
        uuid: experimentUuid,
        page: requestPayload.page as number | undefined,
        page_size: requestPayload.page_size as number | undefined,
        ordering: requestPayload.ordering as string | undefined,
        state: requestPayload.state ? mapResultStatusToJobState(requestPayload.state as string) : undefined,
        search: requestPayload.search as string | undefined,
    };
    const dispatchResult = dispatch(
        experimentApi.endpoints.listExperimentResults.initiate(payload, { forceRefetch: true }),
    );
    try {
        const response = await dispatchResult.unwrap();
        return {
            object_count: response.object_count,
            page_count: response.page_count,
            results: response.results.map((result) => ({
                id: result.uuid,
                name: result.name,
                createdAt: result.created_at,
                finishedAt: result.finished_at,
                state: mapJobStateToResultStatus(result.state),
                appUri: result.app_uri,
            })),
        };
    } finally {
        dispatchResult.unsubscribe();
    }
}
